import { Record } from 'immutable'
import { pipeP, compose, partialRight } from 'ramda'
import Performance, { LOFT_START_MARK, PerformanceEntry } from './performance'
import { getErrorMessages, fetchErrorMessage, notificationTypes } from './loft/constants'
import { BACKEND_AZURE, FRONTEND_EWS, CALENDAR_LOFTING } from './loft/actions'
import preloft from './loft/strategies/preloft'
import appointmentPreloft from './loft/strategies/appointmentPreloft'
import appointmentLofting from './loft/strategies/appointmentLoft'
import frontendEws from './loft/strategies/frontend-ews'
import backendAzure from './loft/strategies/backend-azure'
import LoftRecord, { LoftRecord as LoftRecordClass } from './loft/record'
import gateway from '../gateway/gateway'
import office from '../office/office'
import { GraphAuthChallengeError } from '../office/graphApi/errors'
import { Action } from '../types/loft'
import { isDesktopWindows } from '../office/client'
import { melodyRequest, publicRequest } from '../gateway/request'
import { ServerSession, isSessionValid, refreshAuthData } from './session'
import { addBreadcrumb, captureException } from '../sentry-config'

type FinallyHandler = {
  (event: Office.AddinCommands.Event): void
}

type ErrorHandler = {
  (error: Error): Promise<unknown>
}

type ErrorHandlerCallback = {
  (event: Office.AddinCommands.Event): (error: Error) => Promise<unknown>
}

/**
 * Sanitize error object for logging by removing sensitive data like email body
 * from axios request config
 */
const sanitizeErrorForLogging = (error: any): any => {
  if (!error) return error

  const sanitized = { ...error }

  // Remove sensitive data from axios config (email body, attachments, etc.)
  if (sanitized.config?.data) {
    try {
      // Parse the JSON data if it's a string
      const data =
        typeof sanitized.config.data === 'string'
          ? JSON.parse(sanitized.config.data)
          : sanitized.config.data

      // Redact all values but keep the keys for debugging
      const redactedData: any = {}
      Object.keys(data).forEach(key => {
        redactedData[key] = '[REDACTED]'
      })

      sanitized.config = {
        ...sanitized.config,
        data: redactedData,
      }
    } catch {
      // If parsing fails, just redact the entire data field
      sanitized.config = {
        ...sanitized.config,
        data: '[REDACTED]',
      }
    }
  }

  return sanitized
}

export const createLoftProcess = (
  prelofter: Action[],
  finallyHandler: FinallyHandler,
  errorHandler: ErrorHandler,
  callbacks?: Callbacks
) => (event: Office.AddinCommands.Event) => {
  Performance.mark(LOFT_START_MARK)
  office.currentCommandEvent = event
  const runner = async () => {
    const state = await composePipe(prelofter)(LoftRecord)

    return composePipe(getStrategy(state.strategy))(state)
  }

  return runner()
    .then(() => {
      callbacks?.successCallback()
    })
    .catch(error => {
      callbacks?.errorCallback()
      return errorHandler(error)
    })
    .finally(() => finallyHandler(event))
}

export const createLaunchEventLoftProcess = (
  prelofter: Action[],
  finallyHandler: FinallyHandler,
  errorHandler: ErrorHandlerCallback
) => async (event: Office.AddinCommands.Event) => {
  try {
    const session: ServerSession = await refreshAuthData()
    const errorMessages = getErrorMessages()
    const isLofting = await office.item.getIsLofting()

    if (!isLofting) {
      console.log('Not lofting the event - feature disabled')
      event.completed({
        allowEvent: true,
      })

      return
    }

    if (!isSessionValid(session?.expiration_date || false, session?.access_token || false)) {
      console.log('Not lofting the event - invalid session')
      event.completed({
        allowEvent: false,
        // @ts-expect-error it's a valid property
        errorMessage: errorMessages.INVALID_SESSION,
      })
    }

    melodyRequest.setBaseUrl(session.environment.melody_api_url)
    publicRequest.setBaseUrl(session.environment.public_api_url)

    const user = await gateway.me.get()

    if (
      user?.data?._private_fields?.features.outlook_calendar_loft_button ||
      (isDesktopWindows() &&
        !user?.data?._private_fields?.features.outlook_windows_classic_onsend_lofting) ||
      !isLofting
    ) {
      console.log('Not lofting the event - feature not enabled')
      event.completed({
        allowEvent: true,
      })

      return
    }
  } catch (error) {
    addBreadcrumb({
      message: 'Calendar Loft: createLaunchEventLoftProcess',
      category: 'loft',
      level: 'error',
      data: error?.toString() || '',
    })

    console.error('Error creating launch event loft process', error)
  }

  Performance.mark(LOFT_START_MARK)
  office.currentCommandEvent = event
  const runner = async () => {
    const state = await composePipe(prelofter)(LoftRecord)

    return composePipe(getStrategy(state.strategy))(state)
  }

  return runner()
    .catch(errorHandler(event))
    .finally(() => finallyHandler(event))
}

type ComposePipeResult = {
  (loftRecord: LoftRecordClass): Promise<LoftRecordClass>
}

type Callbacks = {
  successCallback: () => void
  errorCallback: () => void
}

export const composePipe = (pipe: Action[]): ComposePipeResult => {
  const composedPipe = pipe.map(compose(withLoftErrorHandler, withState, withTracing, withServices))

  // eslint-disable-next-line prefer-spread
  return pipeP.apply<null, Action[], ComposePipeResult>(null, composedPipe)
}

export const getStrategy = (name: string): Action[] => {
  const errorMessages = getErrorMessages()
  switch (name) {
    case BACKEND_AZURE:
      return backendAzure as Action[]
    case FRONTEND_EWS:
      return frontendEws as Action[]
    case CALENDAR_LOFTING:
      return appointmentLofting as Action[]
    default:
      throw errorMessages.FAILURE
  }
}

const withServices = (fn: Action): Action => {
  const fnServiced = partialRight(fn, [{ office, gateway }]) as Action
  fnServiced.operation = fn.operation
  return fnServiced
}

const withTracing = (fn: Action) => async (state: LoftRecordClass) => {
  const performanceEntry = Performance.mark()

  const logPayload = (fn: Action, performanceEntry: PerformanceEntry, isError = false) => {
    return {
      operation: fn.operation || 'unknown',
      duration: Performance.measureFrom(performanceEntry.name).duration,
      error: isError,
    }
  }

  try {
    console.info(fn.operation)
    const response = await fn(state)
    addBreadcrumb({ message: 'loft', category: 'lofting', data: logPayload(fn, performanceEntry) })
    return response
  } catch (e) {
    addBreadcrumb({
      message: 'loft',
      category: 'lofting',
      level: 'error',
      data: logPayload(fn, performanceEntry, true),
    })
    throw e
  }
}

const reportFailure = async (error: string, state: LoftRecordClass) => {
  const errorMessages = getErrorMessages()
  switch (error) {
    case errorMessages.INVALID_SESSION:
    case errorMessages.RECIPIENT_INVALID:
    case errorMessages.RECIPIENT_DNC:
    case errorMessages.NO_PERMISSION_CREATE_PERSON:
    case errorMessages.RECIPIENT_INTERNAL_DOMAIN:
      break
    default:
      try {
        await gateway.stats.loftFailure([
          `email_client_type:${await state.getEmailClientType()}`,
          `strategy:${state.strategy}`,
        ])
      } catch (e) {
        // ok to ignore
      }
      break
  }
}

const withLoftErrorHandler = (fn: Action) => async (state: LoftRecordClass) => {
  const errorMessages = getErrorMessages()
  return fn(state).catch(async originalError => {
    await reportFailure(originalError, state)
    try {
      if (!state.email) {
        throw new Error('Email is empty')
      }

      await office.item.setBody(state.email.tracking_body)
    } catch (ignoredError) {
      throw originalError
    }
    if (originalError === errorMessages.SEND_RETRIES_EXHAUSTED) {
      throw errorMessages.SEND_RETRIES_EXHAUSTED
    }
    if (originalError instanceof GraphAuthChallengeError) {
      throw originalError
    }
    throw errorMessages.HANDLED_FAILURE
  })
}

const withState = (fn: Action) => async (state: LoftRecordClass) => {
  const response = await fn(state)

  return Record.isRecord(response) ? response : state
}

export const finallyHandler = (event: Office.AddinCommands.Event): void => {
  event.completed()
}

export const handleLoftErrors = async (e: Error) => {
  console.error('Error handleLoftErrors: ', sanitizeErrorForLogging(e))
  const { errorMessage, shouldReport, shouldNotify } = fetchErrorMessage(e)
  await office.notifications?.remove(notificationTypes.PROGRESS)
  if (shouldNotify) {
    await office.notifications?.addError(notificationTypes.ERROR, errorMessage)
  }
  if (shouldReport) {
    captureException(new Error(errorMessage), {
      extra: {
        name: 'LoftError',
      },
      fingerprint: ['loft#handleLoftErrors'],
    })
  }
}

export default createLoftProcess(preloft as Action[], finallyHandler, handleLoftErrors)

export const appointmentLoft = (callbacks: Callbacks) =>
  createLoftProcess(appointmentPreloft as Action[], finallyHandler, handleLoftErrors, callbacks)

export const appointmentEventLoft = createLaunchEventLoftProcess(
  appointmentPreloft as Action[],
  (event: Office.AddinCommands.Event) => event.completed({ allowEvent: true }),
  (event: Office.AddinCommands.Event) => async (e: Error) => {
    console.error('appointmentEventLoft Error:', sanitizeErrorForLogging(e))
    const { errorMessage, shouldReport } = fetchErrorMessage(e)

    if (shouldReport) {
      captureException(new Error(errorMessage), {
        extra: {
          name: 'LoftError',
        },
        fingerprint: ['loft#handleLoftErrors'],
      })
    }

    await office.notifications?.remove(notificationTypes.PROGRESS)

    event.completed({
      allowEvent: false,
      // @ts-expect-error it's a valid property
      errorMessage: errorMessage,
    })
  }
)
